import React from "react";
import { useLanguage } from "@/context/LanguageContext";
import { ArrowUpRight, Boxes, Workflow, Users, LineChart, CreditCard, Mic, Check } from "lucide-react";

const ICONS = {
  lob_model: Boxes,
  lob_automation: Workflow,
  lob_rh: Users,
  lob_decision: LineChart,
  lob_leadcard: CreditCard,
  lob_speaker: Mic,
};

export const LOBSolutions = ({ onBookDemo }) => {
  const { t } = useLanguage();

  return (
    <section id="solutions" className="relative py-28 md:py-36" data-testid="lob-section">
      <div className="absolute inset-0 -z-10">
        <div className="absolute top-1/3 right-0 w-[400px] h-[400px] rounded-full bg-bicenter-cyan/10 blur-[160px]"></div>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="max-w-3xl mb-16">
          <p className="text-xs uppercase tracking-[0.4em] text-bicenter-magenta font-mono">{t.lob.kicker}</p>
          <h2 className="mt-4 font-display text-4xl md:text-5xl lg:text-6xl font-light tracking-tight text-white leading-[1.05]">
            {t.lob.title}
          </h2>
          <p className="mt-6 text-lg text-zinc-400 font-body leading-relaxed">{t.lob.subtitle}</p>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {t.lob.items.map((s, idx) => {
            const Icon = ICONS[s.key] || Boxes;
            return (
              <div
                key={s.key}
                data-testid={`lob-card-${s.key}`}
                className="group relative bg-[#0c0c0e] border border-white/10 rounded-3xl p-8 lg:p-10 hover:border-white/25 hover:-translate-y-1 transition-all duration-500 overflow-hidden"
              >
                {/* Hover gradient */}
                <div className="absolute -top-px left-0 right-0 h-px bg-gradient-to-r from-transparent via-bicenter-cyan/0 to-transparent group-hover:via-bicenter-cyan/80 transition-all duration-500"></div>
                <div className="absolute -bottom-20 -right-20 w-60 h-60 rounded-full bg-gradient-to-br from-bicenter-cyan/0 to-bicenter-violet/0 group-hover:from-bicenter-cyan/10 group-hover:to-bicenter-violet/10 blur-3xl transition-all duration-700"></div>

                <div className="relative flex items-start justify-between">
                  <div className="w-14 h-14 rounded-2xl border border-white/10 bg-gradient-to-br from-bicenter-cyan/10 to-bicenter-violet/10 flex items-center justify-center">
                    <Icon size={22} className="text-white" />
                  </div>
                  <div className="text-right">
                    <p className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">{t.lob.day_label}</p>
                    <p className="text-sm font-display font-medium text-gradient-mix mt-1">{s.day}</p>
                  </div>
                </div>

                <div className="relative mt-8">
                  <h3 className="font-display text-3xl md:text-4xl font-medium text-white tracking-tight">
                    {s.name}
                  </h3>
                  <p className="mt-2 text-zinc-400 text-base">{s.tagline}</p>
                </div>

                <ul className="relative mt-6 space-y-3">
                  {s.features.map((f, i) => (
                    <li key={i} className="flex items-start gap-3 text-sm text-zinc-300">
                      <span className="mt-0.5 w-5 h-5 rounded-full bg-gradient-to-br from-bicenter-cyan to-bicenter-violet flex items-center justify-center flex-shrink-0">
                        <Check size={12} className="text-white" />
                      </span>
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>

                <button
                  onClick={() => onBookDemo && onBookDemo(s.key)}
                  data-testid={`lob-cta-${s.key}`}
                  className="relative mt-8 inline-flex items-center gap-2 text-sm text-white font-medium border-b border-white/20 pb-1 hover:border-bicenter-cyan transition-all"
                >
                  {t.lob.cta}
                  <ArrowUpRight size={14} />
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
